import React, { useEffect, useRef } from 'react';

const HowItWorks = () => {
  const canvasRef = useRef(null);

  // Canvas animation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let width, height;
    let particles = [];
    const PARTICLE_COUNT = 55;
    let animationFrameId;
    
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width;
      canvas.height = height;
      initParticles();
      drawCanvas();
    };
    
    const initParticles = () => {
      particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          radius: 2 + Math.random() * 5,
          alpha: 0.12 + Math.random() * 0.2,
          speedX: (Math.random() - 0.5) * 0.3,
          speedY: (Math.random() - 0.5) * 0.2,
          color: `rgba(44, 125, 160, ${0.08 + Math.random() * 0.1})`
        });
      }
    };
    
    const updateParticles = () => {
      for (let p of particles) {
        p.x += p.speedX;
        p.y += p.speedY;
        
        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        if (p.y > height + 20) p.y = -20;
      }
    };
    
    const drawCanvas = () => {
      if (!ctx || width === 0 || height === 0) return;
      
      ctx.clearRect(0, 0, width, height);
      
      // Soft radial gradient background
      const centerX = width * 0.6;
      const centerY = height * 0.5;
      const gradient = ctx.createRadialGradient(centerX, centerY, 50, centerX * 0.8, centerY * 0.7, Math.max(width, height) * 0.8);
      gradient.addColorStop(0, 'rgba(44, 125, 160, 0.02)');
      gradient.addColorStop(0.5, 'rgba(95, 168, 201, 0.025)');
      gradient.addColorStop(1, 'rgba(210, 230, 245, 0.01)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Abstract wave lines
      ctx.save();
      ctx.globalAlpha = 0.2;
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        const time = Date.now() * 0.001;
        const offsetY = height * (0.2 + i * 0.15);
        ctx.moveTo(0, offsetY + Math.sin(time * 0.5 + i) * 15);
        for (let x = 0; x <= width; x += 40) {
          const y = offsetY + Math.sin(x * 0.008 + time * 0.8 + i) * 18;
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(44, 125, 160, 0.2)`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      ctx.restore();
      
      // Soft floating circles
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < 12; i++) {
        ctx.beginPath();
        const t = Date.now() * 0.0008;
        const x = width * (0.3 + 0.4 * Math.sin(t * 0.3 + i));
        const y = height * (0.4 + 0.3 * Math.cos(t * 0.5 + i * 1.2));
        const rad = 45 + Math.sin(t * 0.7 + i) * 15;
        const gradientBlur = ctx.createRadialGradient(x, y, 5, x, y, rad);
        gradientBlur.addColorStop(0, 'rgba(44, 125, 160, 0.05)');
        gradientBlur.addColorStop(1, 'rgba(95, 168, 201, 0)');
        ctx.fillStyle = gradientBlur;
        ctx.arc(x, y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      
      // Draw particles
      for (let p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color || `rgba(44, 125, 160, 0.12)`;
        ctx.fill();
      }
      
      // Subtle grid lines
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(44, 125, 160, 0.05)';
      ctx.lineWidth = 0.6;
      for (let i = 0; i < width; i += 65) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, height);
        ctx.stroke();
      }
      for (let i = 0; i < height; i += 65) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(width, i);
        ctx.stroke();
      }
    };
    
    const animate = () => {
      updateParticles();
      drawCanvas();
      animationFrameId = requestAnimationFrame(animate);
    };
    
    const handleResize = () => {
      resizeCanvas();
    };
    
    resizeCanvas();
    animate();
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div className="call_section">
      <div className="wrapper">
        <div className="container margin_80_55">
          <div className="main_title_2">
            <span>
              <em />
            </span>
            <h2>How it Works</h2>
            <p className='text-white'>Effortless journey — from discovery to booking, we make every step seamless and delightful.</p>
          </div>
          
          <div className="row g-4">
            {/* Step 1: Search Locations */}
            <div className="col-md-4">
              <div className="box_how">
                <div className="rect-icon">
                  <i className="fas fa-map-search"></i>
                </div>
                <h3>Search Locations</h3>
                <p>
                  Explore top-rated spots worldwide using smart filters. Find hidden gems, trending cafes, 
                  and exclusive venues — all in one click.
                </p>
              </div>
            </div>
            
            {/* Step 2: View Location Info */}
            <div className="col-md-4">
              <div className="box_how">
                <div className="rect-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>View Location Info</h3>
                <p>
                  Access real-time data, high-res images, expert reviews, and essential amenities. 
                  Make informed decisions with detailed insights.
                </p>
              </div>
            </div>
            
            {/* Step 3: Book, Reach or Call */}
            <div className="col-md-4">
              <div className="box_how">
                <div className="rect-icon">
                  <i className="fas fa-phone-alt"></i>
                </div>
                <h3>Book, Reach or Call</h3>
                <p>
                  Instant reservations, one-tap calling, or GPS navigation. Connect seamlessly and 
                  enjoy a stress-free experience, anytime.
                </p>
              </div>
            </div>
          </div>
          
          {/* Register Button */}
          {/* <p className="text-center add_top_30">
            <a href="/account" className="btn_1 rounded">
              <i className="fas fa-arrow-right"></i> Register Now
            </a>
          </p> */}
        </div>
        
        {/* Canvas Background */}
        <canvas 
          ref={canvasRef} 
          id="hero-canvas" 
          width={1920} 
          height={1080}
        />
      </div>
      
     
    </div>
  );
};

export default HowItWorks;